import { useEffect } from "react";
import { FormHelperText } from "@mui/material";
import DatePicker from "@components/DatePicker/DatePicker";
import CustomFormLabel from "@components/FormItem/FormItem.styles";
import { usePortServicesFlow } from "@features/vessel-call/vessel-call-status/PortServices/flow/context/PortServicesFlowContext";
import {
  getAtcIncludesResourceEndTimesError,
  getAtcMinDate,
} from "@features/vessel-call/vessel-call-status/PortServices/flow/schemas/sharedClosePortServiceSchemas";
import { normalizeDate } from "@features/vessel-call/vessel-call-status/PortServices/flow/utils";
import { useTranslation } from "react-i18next";

const getAtcFutureError = ({
  atc,
  t,
}: {
  atc: Date | null;
  t: (key: string, options?: Record<string, string>) => string;
}) => {
  if (!atc) {
    return undefined;
  }

  return atc > new Date()
    ? t("form-validation.must-not-be-in-future", { label: "ATC" })
    : undefined;
};

const getAtcAfterAtsError = ({
  atc,
  ats,
  t,
}: {
  atc: Date | null;
  ats: Date | null;
  t: (key: string, options?: Record<string, string>) => string;
}) => {
  if (!atc || !ats) {
    return undefined;
  }

  return atc <= ats
    ? t("form-validation.must-be-after", { subject: "ATC", object: "ATS" })
    : undefined;
};

interface ATCFieldProps {
  serviceDetails: { atc?: Date | null | string; ats?: Date | null | string };
}

const getAtcValidationError = ({
  atc,
  ats,
  resourceEndTimes,
  t,
}: {
  atc: Date | null;
  ats: Date | null;
  resourceEndTimes: Array<Date | string | null | undefined>;
  t: (key: string, options?: Record<string, string>) => string;
}) => {
  return (
    getAtcFutureError({ atc, t }) ??
    getAtcAfterAtsError({
      atc,
      ats,
      t,
    }) ??
    getAtcIncludesResourceEndTimesError(atc, resourceEndTimes)
  );
};

const ATCField = ({ serviceDetails: { atc: currentAtc, ats: currentAts } }: ATCFieldProps) => {
  const { t } = useTranslation();
  const { setAtc, atc, errors, setErrors, ats, resources } = usePortServicesFlow();

  useEffect(() => {
    if (currentAtc instanceof Date) {
      setAtc(currentAtc);
    } else if (typeof currentAtc === "string" && currentAtc !== "") {
      setAtc(new Date(currentAtc));
    } else {
      setAtc(null);
    }
  }, []);

  const effectiveAts = ats ?? currentAts;
  const normalizedAtc = normalizeDate(atc);
  const normalizedAts = normalizeDate(effectiveAts);
  const resourceEndTimes = (resources ?? []).map((resource) => resource.usageEndTime);
  const minAtcDate = getAtcMinDate(normalizedAts, resourceEndTimes);

  useEffect(() => {
    if (!normalizedAtc) {
      return;
    }

    const nextAtcError = getAtcValidationError({
      atc: normalizedAtc,
      ats: normalizedAts,
      resourceEndTimes,
      t,
    });

    if (errors.atc === nextAtcError) {
      return;
    }

    setErrors((prev) => {
      if (prev.atc === nextAtcError) {
        return prev;
      }

      return {
        ...prev,
        atc: nextAtcError,
      };
    });
  }, [errors.atc, normalizedAtc, normalizedAts, resourceEndTimes, setErrors, t]);

  return (
    <div className="flex flex-col gap-2">
      <CustomFormLabel data-testid={`change-status-ATC-label`}>ATC</CustomFormLabel>
      <DatePicker
        hasError={errors.atc !== undefined}
        value={normalizedAtc}
        onChange={(d) => {
          setAtc(d);
          setErrors((prev) => ({
            ...prev,
            atc: getAtcValidationError({
              atc: d,
              ats: normalizedAts,
              resourceEndTimes,
              t,
            }),
          }));
        }}
        testId="change-status-ATC-datepicker"
        timePicker={true}
        combinedInput={true}
        dateConstraint="not-future"
        minDate={minAtcDate}
      />
      {errors.atc && (
        <FormHelperText sx={{ color: "error.main", m: 0 }}>
          {typeof errors.atc === "string" ? errors.atc : errors.atc?.toString()}
        </FormHelperText>
      )}
    </div>
  );
};

export default ATCField;
